<template>
  <modal
    name="modal--edit-user"
    transition="nice-modal-fade"
    :click-to-close="false"
    :width="380"
    :height="'auto'"
    :adaptive="true"
    :reset="true"
    :scrollable="true"
    draggable=".drag-handler"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    class="py-10"
  >
    <div
      class="flex flex-col text-black h-full bg-white w-full rounded-sm justify-between p-4"
    >
      <div
        class="text-center text-4xl mb-3 font-extralight tracking-tight drag-handler"
      >
        {{ isCreate ? 'Create User' : 'Edit User' }}
      </div>

      <form @submit.prevent="handleConfirm" autocomplete="off">
        <label
          v-if="isCreate"
          class="mb-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Email
          <input
            class="text-base font-normal focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="email"
            v-model.trim="user.email"
            autocomplete="off"
            required
          />
        </label>

        <div
          v-else
          class="mb-4 p-3 bg-gray-100 rounded-lg text-xs text-gray-500 space-y-1"
        >
          <div>
            <span class="font-bold uppercase tracking-wide">Email:</span>
            {{ user.email || '—' }}
          </div>
          <div>
            <span class="font-bold uppercase tracking-wide">Verified:</span>
            {{ user.emailVerified ? 'Yes' : 'No' }}
          </div>
          <div v-if="user.creationTime">
            <span class="font-bold uppercase tracking-wide">Created:</span>
            {{ user.creationTime }}
          </div>
          <div v-if="user.lastSignInTime">
            <span class="font-bold uppercase tracking-wide">Last Sign In:</span>
            {{ user.lastSignInTime }}
          </div>
          <div v-if="user.id" class="text-gray-400">
            <span class="font-bold uppercase tracking-wide">UID:</span>
            {{ user.id }}
          </div>
        </div>

        <button
          v-if="!isCreate"
          type="button"
          class="buttonOutline w-full focus:shadow-outline focus:outline-none"
          :disabled="resetEmailSent || !user.email"
          @click="sendPasswordReset"
        >
          {{
            resetEmailSent ? 'Reset Email Sent ✓' : 'Send Password Reset Email'
          }}
        </button>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Name
          <input
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
            type="text"
            v-model.lazy="user.name"
            autocomplete="off"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="primaryRole"
        >
          Primary Role
          <select
            id="primaryRole"
            class="mt-2 block appearance-none w-full bg-gray-200 border border-gray-200 text-base font-normal text-gray-700 py-3 px-4 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
            v-model="user.role"
          >
            <option value="" disabled>Select role...</option>
            <option v-for="r in roleOptions" :key="r" :value="r">
              {{ r }}
            </option>
          </select>
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="availableRoles"
        >
          Available Roles
          <multiselect
            id="availableRoles"
            class="text-base font-normal normal-case focus:shadow-outline focus:outline-none mt-2 rounded-lg"
            v-model="user.roles"
            :options="roleOptions"
            :close-on-select="false"
            placeholder="Select roles..."
            :searchable="false"
            :show-labels="false"
            :multiple="true"
            :clear-on-select="false"
            :allow-empty="true"
          />
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="primaryStore"
        >
          Primary Store
          <select
            id="primaryStore"
            class="mt-2 block appearance-none w-full bg-gray-200 border border-gray-200 text-base font-normal text-gray-700 py-3 px-4 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
            v-model="user.store"
          >
            <option value="">None</option>
            <option
              v-for="(storeInfo, storeId) in settingsStoreIds"
              :key="storeId"
              :value="storeId"
            >
              {{ storeInfo.name }}
            </option>
          </select>
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
          for="storeAccess"
        >
          Store Access
          <multiselect
            id="storeAccess"
            class="text-base font-normal normal-case focus:shadow-outline focus:outline-none mt-2 rounded-lg"
            v-model="user.stores"
            :options="storeIdList"
            :custom-label="storeLabel"
            :close-on-select="false"
            placeholder="Select stores..."
            :searchable="false"
            :show-labels="false"
            :multiple="true"
            :clear-on-select="false"
            :allow-empty="true"
          />
        </label>

        <div class="mt-6 flex flex-row justify-between text-lg text-gray-900">
          <span>Account Active?</span>
          <s-switch v-model="userActive" />
        </div>

        <div class="flex flex-row justify-between mt-6">
          <button
            class="buttonOutline focus:shadow-outline focus:outline-none"
            type="button"
            @click="handleCancel"
          >
            Cancel
          </button>
          <button
            v-if="!isCreate"
            class="ml-4 buttonOutline focus:shadow-outline focus:outline-none"
            type="button"
            :disabled="saving"
            @click="handleHide"
          >
            Hide
          </button>
          <button
            class="ml-4 button focus:shadow-outline focus:outline-none"
            type="submit"
            :disabled="saving"
          >
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </form>
    </div>
  </modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Multiselect from 'vue-multiselect'
import SSwitch from '@/components/SSwitch'
import { firebase } from '@/firebase.js'

const roleOptions = ['staff', 'manager', 'admin', 'developer']

const emptyUser = () => ({
  id: null,
  name: '',
  email: '',
  emailVerified: false,
  disabled: false,
  authDisplayName: '',
  role: '',
  roles: [],
  store: '',
  stores: [],
  creationTime: '',
  lastSignInTime: ''
})

export default {
  name: 'ModalEditUser',
  components: { Multiselect, SSwitch },
  data() {
    return {
      roleOptions,
      user: emptyUser(),
      saving: false,
      resetEmailSent: false
    }
  },
  computed: {
    ...mapGetters('admin', ['allUsers']),
    ...mapGetters('global', ['settingsStoreIds']),
    storeIdList() {
      return Object.keys(this.settingsStoreIds)
    },
    isCreate() {
      return !this.user.id
    },
    userActive: {
      get() {
        return !this.user.disabled
      },
      set(val) {
        this.user.disabled = !val
      }
    }
  },
  methods: {
    ...mapActions('admin', ['adminUpdateUser', 'adminCreateUser']),
    ...mapActions('config', ['setFabVisible', 'setDragLock']),
    storeLabel(storeId) {
      return this.settingsStoreIds[storeId] ? this.settingsStoreIds[storeId].name : storeId
    },
    hideModal() {
      this.$modal.hide('modal--edit-user')
    },
    beforeOpen(event) {
      this.setFabVisible(false)
      this.setDragLock(true)
      if (event && event.params && event.params.id) {
        const found = this.allUsers.find((u) => u.id === event.params.id)
        if (found) {
          this.user = { ...emptyUser(), ...found }
          if (!Array.isArray(this.user.roles)) {
            this.user.roles = Object.keys(this.user.roles || {})
          }
          if (!Array.isArray(this.user.stores)) {
            this.user.stores = []
          }
        }
      }
    },
    beforeClose() {
      this.setFabVisible(true)
      this.setDragLock(false)
      this.user = emptyUser()
      this.saving = false
      this.resetEmailSent = false
    },
    handleCancel() {
      this.hideModal()
    },
    async sendPasswordReset() {
      if (!this.user.email) return
      try {
        await firebase.auth().sendPasswordResetEmail(this.user.email)
        this.resetEmailSent = true
      } catch (error) {
        console.error('Error sending password reset:', error)
      }
    },
    async handleHide() {
      if (
        !confirm(
          `Hide account for ${
            this.user.name || this.user.email
          }?\n\nThis will disable their login and remove them from this list.`
        )
      )
        return
      this.saving = true
      try {
        await this.adminUpdateUser({
          userId: this.user.id,
          firestoreData: { status: 'hidden' },
          authData: { disabled: true }
        })
        this.hideModal()
      } catch (error) {
        console.error('Error hiding account:', error)
      } finally {
        this.saving = false
      }
    },
    async handleConfirm() {
      this.saving = true
      try {
        if (this.isCreate) {
          await this.adminCreateUser({
            email: this.user.email,
            name: this.user.name,
            role: this.user.role,
            roles: this.user.roles,
            store: this.user.store,
            stores: this.user.stores,
            disabled: this.user.disabled
          })
        } else {
          await this.adminUpdateUser({
            userId: this.user.id,
            firestoreData: {
              name: this.user.name,
              role: this.user.role,
              roles: this.user.roles,
              store: this.user.store,
              stores: this.user.stores
            },
            authData: {
              disabled: this.user.disabled
            }
          })
        }
        this.hideModal()
      } catch (error) {
        console.error('Error saving user:', error)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
